import { useEffect, useState } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useServiceChargePeriod, useServiceChargeAssignments, useServiceChargeMutations, useServiceChargePeriods } from '@/hooks/serviceCharge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

interface ServiceChargeEmployeeAssignmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId: string;
  periodLabel: string;
}

export const ServiceChargeEmployeeAssignment = ({
  open,
  onOpenChange,
  periodId,
  periodLabel,
}: ServiceChargeEmployeeAssignmentProps) => {
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const { data: period, isLoading: periodLoading } = useServiceChargePeriod(periodId, open);
  const { data: existingAssignments, isLoading: assignmentsLoading } = useServiceChargeAssignments(periodId, open);
  const { data: periods } = useServiceChargePeriods();
  const { assignEmployeeWeights, copyWeightsFromPeriod } = useServiceChargeMutations();

  const { data: employees, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, fte, weekly_hours')
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (existingAssignments) {
      const assignmentMap: Record<string, string> = {};
      existingAssignments.forEach((a) => {
        assignmentMap[a.profile_id] = a.weight_id;
      });
      setAssignments(assignmentMap);
    }
  }, [existingAssignments]);

  const handleSave = async () => {
    const assignmentList = Object.entries(assignments)
      .filter(([_, weightId]) => weightId)
      .map(([profileId, weightId]) => ({
        profile_id: profileId,
        weight_id: weightId,
      }));

    await assignEmployeeWeights.mutateAsync({
      periodId,
      assignments: assignmentList,
    });
    onOpenChange(false);
  };

  const handleCopyFromPeriod = async (sourcePeriodId: string) => {
    await copyWeightsFromPeriod.mutateAsync({
      sourcePeriodId,
      targetPeriodId: periodId,
    });
  };

  const previousPeriods = periods?.filter(p => p.id !== periodId && new Date(p.period_start) < new Date(period?.period_start || ''));

  const isLoading = periodLoading || assignmentsLoading || employeesLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Employee Weights - {periodLabel}</DialogTitle>
          <DialogDescription>
            Assign weight categories to employees for this service charge period
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            {previousPeriods && previousPeriods.length > 0 && (
              <div className="flex items-center gap-2 pb-4">
                <span className="text-sm text-muted-foreground">Copy from:</span>
                <Select onValueChange={handleCopyFromPeriod}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select period..." />
                  </SelectTrigger>
                  <SelectContent>
                    {previousPeriods.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.period_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex-1 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>FTE</TableHead>
                    <TableHead>Weekly Hours</TableHead>
                    <TableHead>Weight Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees?.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>
                        {employee.first_name} {employee.last_name}
                      </TableCell>
                      <TableCell>{employee.fte || '-'}</TableCell>
                      <TableCell>{employee.weekly_hours || '-'}</TableCell>
                      <TableCell>
                        <Select
                          value={assignments[employee.id] || ''}
                          onValueChange={(value) =>
                            setAssignments({ ...assignments, [employee.id]: value })
                          }
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select weight..." />
                          </SelectTrigger>
                          <SelectContent>
                            {period?.weights.map((weight) => (
                              <SelectItem key={weight.id} value={weight.id}>
                                {weight.weight_label}
                                {` (${weight.weight_multiplier}×)`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={assignEmployeeWeights.isPending || isLoading}>
            {assignEmployeeWeights.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Save Assignments
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
